import type { PrivacyConsent } from "@/types/share"
import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Loader2, History, Download, ZoomIn, Calendar, X, ChevronLeft, ChevronRight, RotateCcw, Trash2, LayoutTemplate, ImagePlus, Globe, Share2, Link2, Unlink2, Copy, Check, ExternalLink } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { useInfiniteHistory, useDeleteHistory, type HistoryItem, type GeneratedImageInfo } from "@/hooks/useHistory"
import { useMySharedImages, useShareImage, useUnshareImage } from "@/hooks/useShare"
import { useAuthMe } from "@/hooks/useAuthMe"
import { ShareDialog } from "@/features/share/components/ShareDialog"
import { ShareSuccessDialog } from "@/features/share/components/ShareSuccessDialog"
import { useTemplate } from "@/hooks/useTemplates"
import { useStyles } from "@/hooks/useStyles"
// import { useModels } from "@/hooks/useModels"  // 현재 미사용
import { useGeneratorStore } from "@/stores/generatorStore"
import { downloadImage } from "@/lib/utils"
import { currentLocale } from "@/lib/i18n-locale"
import { localizeName } from "@/lib/i18n-content"
import type { ReferenceImage, ApplyableSettings } from "@/types/generation"
import { TemplatePreviewModal } from "@/features/templates/components/TemplatePreviewModal"
import { extractSettingsFromTemplate } from "@/features/templates/lib/extract-settings-from-template"
import { restoreTemplateParameters } from "@/features/templates/lib/restore-template-parameters"
import { QueuePlaceholderCard } from "./QueuePlaceholderCard"
import { ExpandableText } from "@/components/common/ExpandableText"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"

/**
 * HistoryItem에서 ApplyableSettings로 변환
 * 템플릿, 커뮤니티에서도 유사한 패턴으로 사용 가능
 */
function extractSettingsFromHistory(item: HistoryItem): ApplyableSettings {
  // 레퍼런스 이미지 변환: S3 키 정보 + presigned URL 조합
  const referenceImages: ReferenceImage[] | undefined =
    item.referenceImages?.map((ref, idx) => ({
      id: `reuse-${item.id}-${idx}`,
      s3Key: ref.s3Key,
      thumbnailS3Key: ref.thumbnailS3Key,
      url: item.referenceImageUrls?.[idx] || "",
      thumbnailUrl: item.referenceImageUrls?.[idx] || "",
      width: 256, // 썸네일 기본 크기
      height: 256,
    }))

  return {
    prompt: item.prompt, // 이제 원본 프롬프트가 직접 저장됨
    style: item.style || undefined,
    // aspectRatio가 유효한 값일 때만 적용 (null, undefined, '' 제외)
    aspectRatio: item.aspectRatio || undefined,
    referenceImages,
    // 템플릿 정보 (템플릿으로 생성한 경우). 이름은 사이드바가 현재 언어로 표시한다
    template: item.template ? {
      id: item.template.id,
      name: item.template.name,
      description: item.template.description ?? null,
      nameI18n: item.template.nameI18n,
      descriptionI18n: item.template.descriptionI18n,
      thumbnailUrl: item.template.thumbnailUrl,
    } : undefined,
  }
}

export function HistoryTab() {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteHistory(10)
  const { data: styles } = useStyles()
  const deleteMutation = useDeleteHistory()
  const { data: sharedImagesMap } = useMySharedImages()
  const { data: authMe } = useAuthMe()
  const communityHidden = authMe?.menuHidden?.community ?? false
  const shareMutation = useShareImage()
  const unshareMutation = useUnshareImage()
  // const { data: models } = useModels()  // 현재 미사용
  const applySettings = useGeneratorStore((state) => state.applySettings)
  const referenceImages = useGeneratorStore((state) => state.referenceImages)
  const addReferenceImage = useGeneratorStore((state) => state.addReferenceImage)
  const removeReferenceImagesByGenerationId = useGeneratorStore((state) => state.removeReferenceImagesByGenerationId)
  const prompt = useGeneratorStore((state) => state.prompt)
  const selectedTemplate = useGeneratorStore((state) => state.selectedTemplate)
  const setPrompt = useGeneratorStore((state) => state.setPrompt)
  const clearTemplate = useGeneratorStore((state) => state.clearTemplate)
  const requestPromptFocus = useGeneratorStore((state) => state.requestPromptFocus)
  const observerTarget = useRef<HTMLDivElement>(null)

  // 큐 상태 및 액션
  const queue = useGeneratorStore((state) => state.queue)
  const cancelQueueItem = useGeneratorStore((state) => state.cancelQueueItem)
  const retryQueueItem = useGeneratorStore((state) => state.retryQueueItem)

  // 활성 큐 아이템 (pending + processing) - 최신 항목이 위에 오도록 역순
  const activeQueueItems = queue
    .filter((item) => item.status === 'pending' || item.status === 'processing')
    .reverse()
  // 히스토리가 비어있는지 여부 (큐만 있는 경우에도 UI 표시)
  const hasActiveQueue = activeQueueItems.length > 0

  // Lightbox 상태: 생성된 이미지들과 현재 인덱스
  const [lightbox, setLightbox] = useState<{
    images: GeneratedImageInfo[]
    currentIndex: number
  } | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  // 레퍼런스 이미지 호버 레이어 상태
  const [hoveredReference, setHoveredReference] = useState<{
    url: string
    rect: DOMRect
    width: number
    height: number
  } | null>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 삭제 확인 다이얼로그 상태 (단일 히스토리 항목)
  const [deleteTarget, setDeleteTarget] = useState<HistoryItem | null>(null)
  // 템플릿 상세 모달 상태 (templateId)
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null)
  // 공유 다이얼로그 상태
  const [shareTarget, setShareTarget] = useState<{
    generationId: string
    s3Key: string
    imageUrl: string
  } | null>(null)
  // 공유 성공 다이얼로그 상태
  const [shareSuccess, setShareSuccess] = useState<{
    shareId: string
    imageUrl: string
  } | null>(null)
  // 공유 정보 다이얼로그 상태 (URL 표시 + 공유 취소 버튼)
  const [shareInfoTarget, setShareInfoTarget] = useState<{
    shareId: string
    imageUrl: string
  } | null>(null)
  const [shareInfoCopied, setShareInfoCopied] = useState(false)
  // 공유 취소 확인 다이얼로그 상태
  const [unshareTarget, setUnshareTarget] = useState<{
    shareId: string
    imageUrl: string
  } | null>(null)
  // 이어그리기 확인 다이얼로그 상태 (프롬프트/템플릿 초기화 확인)
  const [addRefTarget, setAddRefTarget] = useState<GeneratedImageInfo | null>(null)

  // 템플릿 상세 조회 (모달용)
  const { data: previewTemplate } = useTemplate(previewTemplateId)

  // Lightbox 네비게이션
  const navigateLightbox = useCallback((direction: "prev" | "next") => {
    if (!lightbox) return
    const { images, currentIndex } = lightbox
    let newIndex: number
    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1
    } else {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0
    }
    setLightbox({ images, currentIndex: newIndex })
  }, [lightbox])

  const closeLightbox = useCallback(() => {
    setLightbox(null)
  }, [])

  // 레퍼런스 이미지 호버 핸들러
  const handleReferenceMouseEnter = useCallback((url: string, element: HTMLElement) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    const rect = element.getBoundingClientRect()

    // 이미지 실제 크기 파악 후 비율에 맞게 레이어 크기 계산
    const img = new Image()
    img.onload = () => {
      const maxSize = 280
      const aspectRatio = img.naturalWidth / img.naturalHeight
      let width: number, height: number

      if (aspectRatio > 1) {
        // 가로가 더 긴 경우
        width = maxSize
        height = Math.round(maxSize / aspectRatio)
      } else {
        // 세로가 더 길거나 정방형
        height = maxSize
        width = Math.round(maxSize * aspectRatio)
      }

      setHoveredReference({ url, rect, width, height })
    }
    img.src = url // 이미 캐시되어 있으면 즉시 로드됨
  }, [])

  const handleReferenceMouseLeave = useCallback(() => {
    // 약간의 딜레이를 줘서 레이어로 마우스 이동 시 깜빡임 방지
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredReference(null)
    }, 100)
  }, [])

  const handleLayerMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  const handleLayerMouseLeave = useCallback(() => {
    setHoveredReference(null)
  }, [])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // 키보드 네비게이션 (Lightbox)
  useEffect(() => {
    if (!lightbox) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        navigateLightbox("prev")
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        navigateLightbox("next")
      } else if (e.key === "Escape") {
        e.preventDefault()
        closeLightbox()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [lightbox, navigateLightbox, closeLightbox])

  const getStyleName = (styleId: string | null): string | null => {
    if (!styleId) return null
    const style = styles?.find((s) => s.id === styleId)
    return (style && localizeName(style)) || styleId
  }

  // getModelName - 현재 미사용 (UI에서 모델명 chip 주석처리됨)
  // const getModelName = (item: HistoryItem) => {
  //   const params = item.params as Record<string, unknown> | null
  //   const model = params?.model as string | undefined
  //   return models?.find((m) => m.id === model)?.name || model || item.modelId
  // }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(currentLocale(), {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // aspectRatio 문자열을 CSS aspect-ratio 값으로 변환
  const getAspectRatioStyle = (ratio: string): React.CSSProperties => {
    const [w, h] = ratio.split(":").map(Number)
    if (w && h) {
      return { aspectRatio: `${w}/${h}` }
    }
    return { aspectRatio: "1/1" }
  }

  // 이미지 개수와 비율에 따라 동적으로 그리드 클래스 결정
  const getGridClass = (count: number, aspectRatio: string): string => {
    const [w, h] = aspectRatio.split(":").map(Number)
    const isPortrait = h > w // 세로가 긴 비율 (9:16, 3:4)

    if (count === 1) {
      // 1장일 때는 적절한 최대 너비로 제한
      return isPortrait
        ? "grid-cols-1 max-w-[200px]"
        : "grid-cols-1 max-w-sm"
    }
    if (count === 2) {
      return isPortrait
        ? "grid-cols-2 max-w-md"
        : "grid-cols-2"
    }
    if (isPortrait) {
      // 세로 비율은 최대 3열 (너무 좁아지지 않도록)
      return count <= 3
        ? "grid-cols-3"
        : "grid-cols-3 sm:grid-cols-4"
    }
    // 가로/정사각 비율은 최대 4열
    return count <= 4
      ? "grid-cols-2 sm:grid-cols-4"
      : "grid-cols-3 sm:grid-cols-4"
  }

  const handleDownload = async (image: GeneratedImageInfo) => {
    // s3Key에서 imageId 추출하여 파일명 생성
    const imageId = image.s3Key.split('/').pop()?.replace('.png', '') || 'image'
    setDownloadingId(imageId)

    try {
      await downloadImage(image.imageUrl, `wrks-${imageId}.png`)
      toast.success(t('history.downloadSuccessTitle'), {
        description: t('history.downloadSuccessDescription'),
      })
    } catch {
      toast.error(t('history.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  /** 실제 레퍼런스 이미지 추가 (프롬프트/템플릿 초기화 포함) */
  const executeAddToReference = useCallback((img: GeneratedImageInfo, resetForm: boolean) => {
    const refImage: ReferenceImage = {
      id: `generated-${img.s3Key}`,
      s3Key: img.s3Key,
      thumbnailS3Key: img.s3Key,
      url: img.imageUrl,
      thumbnailUrl: img.imageUrl,
      width: 256,
      height: 256,
    }

    if (resetForm) {
      setPrompt("")
      clearTemplate()
    }

    addReferenceImage(refImage)
    requestPromptFocus()
    toast.success(t('history.referenceAdded'))
  }, [addReferenceImage, setPrompt, clearTemplate, requestPromptFocus, t])

  const handleAddToReference = (img: GeneratedImageInfo) => {
    // 최대 4개 제한 체크
    if (referenceImages.length >= 4) {
      toast.error(t('history.referenceLimit'))
      return
    }

    // 중복 체크 (같은 s3Key가 이미 있는지)
    if (referenceImages.some((ref) => ref.s3Key === img.s3Key)) {
      toast.error(t('history.referenceDuplicate'))
      return
    }

    // 프롬프트나 템플릿이 있으면 확인 다이얼로그 표시
    if (prompt.trim() || selectedTemplate) {
      setAddRefTarget(img)
      return
    }

    executeAddToReference(img, false)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return

    const count = deleteTarget.generatedImages.length
    const generationPrefix = `generations/${deleteTarget.id}/`
    const staleReferenceCount = referenceImages.filter(
      (ref) => ref.s3Key.startsWith(generationPrefix) || ref.thumbnailS3Key.startsWith(generationPrefix)
    ).length
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      if (staleReferenceCount > 0) {
        removeReferenceImagesByGenerationId(deleteTarget.id)
      }
      toast.success(t('history.deleteSuccessTitle'), {
        description: staleReferenceCount > 0
          ? t('history.deleteSuccessWithRefs', { count, refCount: staleReferenceCount })
          : t('history.deleteSuccessDescription', { count }),
      })
      setDeleteTarget(null)
    } catch {
      toast.error(t('history.deleteFailed'))
    }
  }

  const handleShareConfirm = async (consent: PrivacyConsent) => {
    if (!shareTarget) return

    const result = await shareMutation.mutateAsync({
      ...consent,
      generationId: shareTarget.generationId,
      s3Key: shareTarget.s3Key,
    })
    setShareTarget(null)
    setShareSuccess({
      shareId: result.id,
      imageUrl: shareTarget.imageUrl,
    })
  }

  const handleUnshareConfirm = async () => {
    if (!unshareTarget) return

    try {
      await unshareMutation.mutateAsync(unshareTarget.shareId)
      toast.success(t('history.unshareSuccess'))
      setUnshareTarget(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('history.unshareFailed'))
    }
  }

  const isImageShared = (s3Key: string): boolean => {
    return sharedImagesMap?.has(s3Key) ?? false
  }

  const getShareId = (s3Key: string): string | undefined => {
    return sharedImagesMap?.get(s3Key)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <p>{t('history.loadFailed')}</p>
        <p className="text-sm text-slate-400 mt-1">{t('common.tryAgainLater')}</p>
      </div>
    )
  }

  // Flatten all pages into a single array
  const allItems = data?.pages.flatMap((page) => page.items) || []

  if (!allItems.length && !hasActiveQueue) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <History className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('history.emptyTitle')}
        </h3>
        <p className="text-slate-500 text-sm">
          {t('history.emptyDescriptionLine1')}
          <br />
          {t('history.emptyDescriptionLine2')}
        </p>
      </div>
    )
  }

  // 이제 1 row = 1 generation이므로 그룹핑 불필요

  return (
    <div className="space-y-8">
      {/* 큐 Placeholder (pending + processing 아이템들) */}
      {activeQueueItems.map((item, index) => (
        <QueuePlaceholderCard
          key={item.id}
          item={item}
          position={index + 1}
          totalInQueue={activeQueueItems.length}
          onCancel={item.status === 'pending' ? () => cancelQueueItem(item.id) : undefined}
          onRetry={item.status === 'failed' ? () => retryQueueItem(item.id) : undefined}
        />
      ))}

      {/* 히스토리 (1 item = 1 generation request) */}
      {allItems.map((item) => {
        const imageId = (img: GeneratedImageInfo) => img.s3Key.split('/').pop()?.replace('.png', '') || ''

        return (
          <div
            key={item.id}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            {/* 생성 정보 헤더 */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                {/* 프롬프트 + 태그 (모바일: 전체 너비) */}
                <div className="flex-1 min-w-0">
                  <ExpandableText
                    text={item.prompt}
                    className="text-slate-800 text-sm"
                    lineClamp={2}
                  />
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {getStyleName(item.style) && (
                      <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                        {getStyleName(item.style)}
                      </span>
                    )}
                    <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">
                      {item.aspectRatio}
                    </span>
                  </div>
                </div>
                {/* 액션 + 메타 (모바일에서도 우측 정렬) */}
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        const settings = await restoreTemplateParameters(extractSettingsFromHistory(item))
                        applySettings(settings)
                        toast.success(t('history.reuseSuccessTitle'), {
                          description: t('history.reuseSuccessDescription'),
                        })
                      }}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>{t('history.reuse')}</span>
                    </button>
                    <span className="text-slate-200">|</span>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(item.createdAt)}
                    </div>
                    <span className="text-slate-200">|</span>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      disabled={deleteMutation.isPending}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* 템플릿 + 레퍼런스 이미지 */}
                  {(item.template || (item.referenceImageUrls && item.referenceImageUrls.length > 0)) && (
                    <div className="flex items-center gap-3">
                      {/* 템플릿 썸네일 */}
                      {item.template && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500">{t('history.templateLabel')}</span>
                          <button
                            onClick={() => setPreviewTemplateId(item.template!.id)}
                            className="w-10 h-10 rounded border border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10 transition-colors overflow-hidden flex items-center justify-center cursor-pointer"
                            title={localizeName(item.template)}
                          >
                            {item.template.thumbnailUrl ? (
                              <img
                                src={item.template.thumbnailUrl}
                                alt={localizeName(item.template)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <LayoutTemplate className="w-5 h-5 text-primary/60" />
                            )}
                          </button>
                        </div>
                      )}
                      {/* 레퍼런스 이미지 */}
                      {item.referenceImageUrls && item.referenceImageUrls.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500">{t('history.referenceLabel')}</span>
                          <div className="flex gap-1">
                            {item.referenceImageUrls.map((url, idx) => (
                              <div
                                key={idx}
                                onMouseEnter={(e) => handleReferenceMouseEnter(url, e.currentTarget)}
                                onMouseLeave={handleReferenceMouseLeave}
                                className="w-10 h-10 rounded border border-slate-200 bg-slate-50 hover:border-slate-400 transition-colors overflow-hidden flex items-center justify-center cursor-zoom-in"
                              >
                                <img
                                  src={url}
                                  alt={t('history.referenceImageAlt', { index: idx + 1 })}
                                  className="max-w-full max-h-full object-contain pointer-events-none"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 이미지 그리드 */}
            <div className="p-4">
              <div className={`grid ${getGridClass(item.generatedImages.length, item.aspectRatio)} gap-3`}>
                {item.generatedImages.map((img, i) => {
                  const shared = isImageShared(img.s3Key)
                  return (
                    <div
                      key={imageId(img)}
                      className="group relative bg-transparent rounded-lg overflow-hidden cursor-pointer focus:outline-none"
                      style={getAspectRatioStyle(item.aspectRatio)}
                      onClick={() => setLightbox({ images: item.generatedImages, currentIndex: i })}
                    >
                      <img
                        src={img.imageUrl}
                        alt={t('history.generatedImageAlt', { index: i + 1 })}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {/* 공유된 이미지 뱃지 (데스크톱에서만 클릭 가능) */}
                      {shared && (
                        <div className="absolute top-2 left-2 z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              const shareId = getShareId(img.s3Key)
                              if (shareId) {
                                setShareInfoTarget({
                                  shareId,
                                  imageUrl: img.imageUrl,
                                })
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 bg-primary/90 lg:hover:bg-primary text-white text-xs rounded-full transition-colors pointer-events-none lg:pointer-events-auto"
                            title={t('history.viewShareInfo')}
                          >
                            <Globe className="w-3 h-3" />
                            {t('history.sharedBadge')}
                          </button>
                        </div>
                      )}
                      {/* 이어그리기 상시 버튼 (우하단) */}
                      <div className="absolute bottom-1.5 right-1.5 z-10">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAddToReference(img)
                                }}
                                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm hover:bg-white text-slate-600 hover:text-slate-800 transition-colors shadow-sm"
                              >
                                <ImagePlus className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">{t('history.continueDrawing')}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {/* Hover overlay - 데스크톱 전용 (모바일은 lightbox에서 액션 수행) */}
                      <div className="hidden lg:flex absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 items-center justify-center opacity-0 group-hover:opacity-100">
                        <TooltipProvider delayDuration={0}>
                          <div className="flex gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setLightbox({ images: item.generatedImages, currentIndex: i })
                                  }}
                                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-slate-700 transition-colors"
                                >
                                  <ZoomIn className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top">{t('history.zoom')}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDownload(img)
                                  }}
                                  disabled={downloadingId === imageId(img)}
                                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-slate-700 transition-colors disabled:opacity-50"
                                >
                                  {downloadingId === imageId(img) ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top">{t('history.download')}</TooltipContent>
                            </Tooltip>
                            {/* 공유 버튼 */}
                            {!shared && !communityHidden && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setShareTarget({
                                        generationId: item.id,
                                        s3Key: img.s3Key,
                                        imageUrl: img.imageUrl,
                                      })
                                    }}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-slate-700 transition-colors"
                                  >
                                    <Share2 className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{t('history.share')}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      </div>
                    </div>
                  )
                })}
              </div>
              {/* AI 생성 표시 (AI 기본법 제31조) */}
              <AiGeneratedNotice className="mt-3" />
            </div>
          </div>
        )
      })}

      {/* Infinite scroll trigger & loading indicator */}
      {hasNextPage && (
        <div
          ref={observerTarget}
          className="flex items-center justify-center py-8"
        >
          {isFetchingNextPage && (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-8 animate-fade-in focus:outline-none"
          onClick={closeLightbox}
        >
          {/* 닫기 버튼 */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* 좌측 네비게이션 */}
          {lightbox.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigateLightbox("prev")
              }}
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* 이미지 */}
          <img
            src={lightbox.images[lightbox.currentIndex].imageUrl}
            alt={t('history.lightboxImageAlt', { index: lightbox.currentIndex + 1, total: lightbox.images.length })}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* 우측 네비게이션 */}
          {lightbox.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigateLightbox("next")
              }}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* 하단 컨트롤: 인덱스 표시 + 액션 버튼들 */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
            {/* AI 생성 표시 (AI 기본법 제31조) */}
            <AiGeneratedNotice className="text-white/70" />
            {lightbox.images.length > 1 && (
              <div className="text-white/70 text-sm">
                {lightbox.currentIndex + 1} / {lightbox.images.length}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  handleDownload(lightbox.images[lightbox.currentIndex])
                }}
                className="bg-white hover:bg-slate-100 text-slate-800"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('history.download')}
              </Button>
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddToReference(lightbox.images[lightbox.currentIndex])
                }}
                className="bg-white hover:bg-slate-100 text-slate-800"
              >
                <ImagePlus className="w-4 h-4 mr-2" />
                {t('history.continueDrawing')}
              </Button>
              {!isImageShared(lightbox.images[lightbox.currentIndex].s3Key) && !communityHidden && (
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation()
                    // lightbox에서 현재 이미지의 generationId를 찾아야 함
                    const currentImage = lightbox.images[lightbox.currentIndex]
                    const item = allItems.find(item =>
                      item.generatedImages.some(img => img.s3Key === currentImage.s3Key)
                    )
                    if (item) {
                      setShareTarget({
                        generationId: item.id,
                        s3Key: currentImage.s3Key,
                        imageUrl: currentImage.imageUrl,
                      })
                      closeLightbox()
                    }
                  }}
                  className="bg-white hover:bg-slate-100 text-slate-800"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('history.share')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reference Image Floating Layer */}
      {hoveredReference && (() => {
        const { url, rect, width, height } = hoveredReference
        const padding = 8 // 레이어 내부 패딩
        const layerWidth = width + padding * 2
        const layerHeight = height + padding * 2
        const gap = 8 // 썸네일과의 간격

        // 레이어 위치 계산 (썸네일 아래에 표시, 화면 밖으로 나가면 조정)
        let left = rect.left
        let top = rect.bottom + gap

        // 오른쪽으로 벗어나면 왼쪽으로 조정
        if (left + layerWidth > window.innerWidth - 16) {
          left = window.innerWidth - layerWidth - 16
        }
        // 왼쪽으로 벗어나면 오른쪽으로 조정
        if (left < 16) {
          left = 16
        }
        // 아래로 벗어나면 위에 표시
        if (top + layerHeight > window.innerHeight - 16) {
          top = rect.top - layerHeight - gap
        }

        return (
          <div
            className="fixed z-50 animate-fade-in"
            style={{ left, top }}
            onMouseEnter={handleLayerMouseEnter}
            onMouseLeave={handleLayerMouseLeave}
          >
            <div
              className="bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
              style={{ padding }}
            >
              <img
                src={url}
                alt={t('history.referenceZoomAlt')}
                className="rounded"
                style={{ width, height }}
              />
            </div>
          </div>
        )
      })()}

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.deleteDialogTitle')}</DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.deleteDialogDescription1')}
              <br />
              {t('history.deleteDialogDescription2')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('history.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 템플릿 프리뷰 모달 */}
      <TemplatePreviewModal
        template={previewTemplate ?? null}
        isOpen={previewTemplateId !== null}
        onClose={() => setPreviewTemplateId(null)}
        onUse={previewTemplate ? () => {
          const settings = extractSettingsFromTemplate(previewTemplate)
          applySettings(settings)
          toast.success(t('history.templateAppliedTitle'), {
            description: t('history.templateAppliedDescription', { name: localizeName(previewTemplate) }),
          })
        } : undefined}
      />

      {/* 공유 확인 다이얼로그 */}
      <ShareDialog
        open={!!shareTarget}
        onOpenChange={(open) => !open && setShareTarget(null)}
        onConfirm={handleShareConfirm}
        generationId={shareTarget?.generationId ?? ""}
        s3Key={shareTarget?.s3Key ?? ""}
        isLoading={shareMutation.isPending}
        imageUrl={shareTarget?.imageUrl ?? ""}
      />

      {/* 공유 성공 다이얼로그 */}
      <ShareSuccessDialog
        open={!!shareSuccess}
        onOpenChange={(open) => !open && setShareSuccess(null)}
        shareId={shareSuccess?.shareId ?? ""}
        imageUrl={shareSuccess?.imageUrl ?? ""}
      />

      {/* 공유 정보 다이얼로그 */}
      <Dialog open={!!shareInfoTarget} onOpenChange={(open) => !open && setShareInfoTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              {t('history.shareLinkTitle')}
            </DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.shareLinkDescription')}
            </DialogDescription>
          </DialogHeader>
          {shareInfoTarget && (
            <>
              {/* 이미지 미리보기 */}
              <div className="flex justify-center py-4">
                <img
                  src={shareInfoTarget.imageUrl}
                  alt={t('history.sharedImageAlt')}
                  className="max-w-full max-h-32 rounded-lg border border-slate-200"
                />
              </div>
              {/* URL 복사 영역 */}
              {(() => {
                const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
                const shareUrl = `${window.location.origin}${basePath}/share/${shareInfoTarget.shareId}`
                return (
                  <div className="flex items-center gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1 text-sm bg-slate-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(shareUrl)
                          setShareInfoCopied(true)
                          toast.success(t('history.urlCopied'))
                          setTimeout(() => setShareInfoCopied(false), 2000)
                        } catch {
                          toast.error(t('history.copyFailed'))
                        }
                      }}
                    >
                      {shareInfoCopied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => window.open(shareUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                )
              })()}
            </>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="default"
              onClick={() => {
                if (shareInfoTarget) {
                  setUnshareTarget({
                    shareId: shareInfoTarget.shareId,
                    imageUrl: shareInfoTarget.imageUrl,
                  })
                  setShareInfoTarget(null)
                }
              }}
            >
              <Unlink2 className="w-4 h-4 mr-1.5" />
              {t('history.unshare')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShareInfoTarget(null)}
            >
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 공유 취소 확인 다이얼로그 */}
      <Dialog open={!!unshareTarget} onOpenChange={(open) => !open && setUnshareTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.unshare')}</DialogTitle>
            <DialogDescription className="mt-2">
              {t('history.unshareDialogDescription1')}
              <br />
              {t('history.unshareDialogDescription2')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setUnshareTarget(null)}
              disabled={unshareMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnshareConfirm}
              disabled={unshareMutation.isPending}
            >
              {unshareMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('history.processing')}
                </>
              ) : (
                t('common.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 이어그리기 확인 다이얼로그 (프롬프트/템플릿 초기화) */}
      <Dialog open={!!addRefTarget} onOpenChange={(open) => !open && setAddRefTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.continueDrawing')}</DialogTitle>
            <DialogDescription className="mt-2">
              {selectedTemplate ? t('history.addRefResetPromptAndTemplate') : t('history.addRefResetPrompt')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setAddRefTarget(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (addRefTarget) {
                  executeAddToReference(addRefTarget, false)
                  setAddRefTarget(null)
                }
              }}
            >
              {t('history.keepAndAdd')}
            </Button>
            <Button
              onClick={() => {
                if (addRefTarget) {
                  executeAddToReference(addRefTarget, true)
                  setAddRefTarget(null)
                }
              }}
            >
              {t('history.resetAndAdd')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
